import React, { useState } from 'react';
import { Box, Container, Typography, Modal, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import Reveal from './Reveal';

interface FeatureShowcaseProps {
  eyebrow: string;
  title: string;
  description: string;
  imageSrc: string;
  imageAlt: string;
  reverse?: boolean;
  backgroundColor?: string;
}

const FeatureShowcase: React.FC<FeatureShowcaseProps> = ({
  eyebrow,
  title,
  description,
  imageSrc,
  imageAlt,
  reverse = false,
  backgroundColor = '#ffffff',
}) => {
  const [open, setOpen] = useState(false);

  return (
    <>
      <Modal open={open} onClose={() => setOpen(false)}>
        <Box
          sx={{
            position: 'fixed',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'rgba(0,0,0,0.85)',
            p: { xs: 2, md: 4 },
          }}
          onClick={() => setOpen(false)}
        >
          <IconButton
            onClick={() => setOpen(false)}
            sx={{ position: 'absolute', top: 16, right: 16, color: '#fff' }}
          >
            <CloseIcon />
          </IconButton>
          <Box
            component="img"
            src={imageSrc}
            alt={imageAlt}
            onClick={(e) => e.stopPropagation()}
            sx={{
              maxWidth: '95vw',
              maxHeight: '90vh',
              borderRadius: 2,
              boxShadow: '0 20px 60px rgba(0,0,0,0.5)',
              objectFit: 'contain',
            }}
          />
        </Box>
      </Modal>
      <Box sx={{ py: { xs: 6, md: 10 }, backgroundColor }}>
      <Container maxWidth="lg">
        <Reveal>
        <Box
          sx={{
            display: 'flex',
            flexDirection: {
              xs: 'column',
              md: reverse ? 'row-reverse' : 'row',
            },
            alignItems: 'center',
            gap: { xs: 4, md: 8 },
          }}
        >
          <Box sx={{ flex: 1, width: '100%' }}>
            <Typography
              variant="overline"
              sx={{
                color: 'text.secondary',
                fontWeight: 600,
                letterSpacing: '0.08em',
                mb: 1,
                display: 'block',
              }}
            >
              {eyebrow}
            </Typography>
            <Typography
              variant="h3"
              component="h2"
              sx={{
                fontWeight: 700,
                mb: 2,
                fontSize: { xs: '1.75rem', md: '2.25rem' },
                lineHeight: 1.2,
                letterSpacing: '-0.01em',
              }}
            >
              {title}
            </Typography>
            <Typography
              variant="h6"
              component="p"
              sx={{
                color: 'text.secondary',
                fontSize: { xs: '1rem', md: '1.125rem' },
                lineHeight: 1.6,
                fontWeight: 400,
              }}
            >
              {description}
            </Typography>
          </Box>
          <Box
            onClick={() => setOpen(true)}
            sx={{
              flex: 1.2,
              width: '100%',
              borderRadius: 3,
              overflow: 'hidden',
              border: '1px solid rgba(0, 0, 0, 0.08)',
              boxShadow: '0 10px 40px rgba(0, 0, 0, 0.08)',
              backgroundColor: '#ffffff',
              cursor: 'zoom-in',
              transition: 'box-shadow 0.2s',
              '&:hover': {
                boxShadow: '0 16px 48px rgba(0, 0, 0, 0.15)',
              },
            }}
          >
            <Box
              component="img"
              src={imageSrc}
              alt={imageAlt}
              loading="lazy"
              sx={{
                display: 'block',
                width: '100%',
                height: 'auto',
              }}
            />
          </Box>
        </Box>
        </Reveal>
      </Container>
    </Box>
    </>
  );
};

export default FeatureShowcase;
